import {
  CareOrderStatus,
  CareOrderStatusEnum,
  type ColumnDef,
  getCareOrderStatusText,
  getOrderStatusText,
  getQuoteStatusText,
  isOfEnum,
  OrderStatus,
  OrdStatusEnum,
  QuoteStatus,
  QuoteStatusEnum,
  stringColumnComparator,
} from '@talos/kyoko';
import type { IAggFuncParams, ICellRendererParams, ValueFormatterParams, ValueGetterParams } from 'ag-grid-community';
import { compact } from 'lodash-es';
import {
  type CareOrderBlotterEntity,
  type CareOrderRow,
  isCareOrderRow,
  isLegRow,
  isOrderRow,
  isQuoteRow,
  type LegRow,
  type OrderRow,
  type QuoteRow,
} from '../types';
import { isCareOrderRowFilledAndPendingFix } from '../utils';
import { legRowFadedCellClassRules } from './legRowFadedCellClassRules';

interface StatusColumnParams {
  initialPinned: ColumnDef<CareOrderBlotterEntity>['initialPinned'];
}

/** Different construct at the two different levels */
type StatusColumnValue =
  | { type: 'child'; status: OrdStatusEnum | QuoteStatusEnum; statusText: string; data: QuoteRow | OrderRow | LegRow }
  | {
      type: 'parent';
      status: CareOrderStatusEnum;
      statusText: string;
      data: CareOrderRow;
      pendingFix: boolean;
    }
  | undefined;

export const CARE_ORDER_STATUS_AGG_FUNC = 'careOrderStatus';

/** Basic ValueGetter, just pulled out so it can be-reused */
function valueGetter(params: ValueGetterParams<CareOrderBlotterEntity>): StatusColumnValue {
  if (isCareOrderRow(params.data)) {
    // This will run on CareOrder rows that don't have any children yet. For example, brand new care orders, or care orders with children, but they haven't been paginated to yet and added.
    return {
      type: 'parent',
      status: params.data.status,
      statusText: getCareOrderStatusText({
        status: params.data.status,
        pendingFix: false,
        formatMessage: params.context.current.intl.formatMessage,
      }),
      data: params.data,
      pendingFix: false,
    };
  }
  if (isOrderRow(params.data)) {
    return {
      type: 'child',
      status: params.data.OrdStatus,
      statusText: getOrderStatusText({ type: 'order', order: params.data, isCustomerOrder: false }),
      data: params.data,
    };
  }
  if (isQuoteRow(params.data) || isLegRow(params.data)) {
    return {
      type: 'child',
      status: params.data.QuoteStatus,
      statusText: getQuoteStatusText({ quoteStatus: params.data.QuoteStatus }),
      data: params.data,
    };
  }
  return undefined;
}

export const careOrderStatusAggFunc = ({
  values,
  rowNode,
  context,
}: IAggFuncParams<CareOrderBlotterEntity, StatusColumnValue>): StatusColumnValue => {
  // The aggFunc is called for the CareOrder row. The children are QuoteRow | OrderRow
  if (isQuoteRow(rowNode.data)) {
    return {
      type: 'child',
      status: rowNode.data.QuoteStatus,
      statusText: getQuoteStatusText({ quoteStatus: rowNode.data.QuoteStatus }),
      data: rowNode.data,
    };
  }

  if (!isCareOrderRow(rowNode.data)) {
    return undefined;
  }

  // In order to determine if a Care Order is Pending Fix, we have to look at its children.
  // The frontend needs a backend enhancement here to bubble this property up to the CO.
  const childData = compact(values.map(v => (v?.type === 'child' && !isLegRow(v.data) ? v.data : undefined)));
  const pendingFix = isCareOrderRowFilledAndPendingFix(
    rowNode.data,
    childData.map(ce => ({ data: ce }))
  );

  return {
    type: 'parent',
    status: rowNode.data.status,
    statusText: getCareOrderStatusText({
      status: rowNode.data.status,
      pendingFix,
      formatMessage: context.current.intl.formatMessage,
    }),
    data: rowNode.data,
    pendingFix,
  };
};

export const getCareOrderBlotterEntityStatusColumn: (
  params: StatusColumnParams
) => ColumnDef<CareOrderBlotterEntity> = column => ({
  type: 'custom',
  id: 'status',
  initialPinned: column.initialPinned,
  initialAggFunc: CARE_ORDER_STATUS_AGG_FUNC,
  params: {
    initialWidth: 130,
    valueGetter,
    filterValueGetter: (params: ValueGetterParams<CareOrderBlotterEntity>) => {
      // For filtering application, we just expose the status itself, and not the whole object used within the column
      return valueGetter(params)?.status;
    },
    cellRenderer: ({ value }: ICellRendererParams<CareOrderBlotterEntity, StatusColumnValue>) => {
      if (!value) {
        return null;
      }

      if (isCareOrderRow(value.data) && isOfEnum(value.status, CareOrderStatusEnum)) {
        return (
          <CareOrderStatus
            status={value.status}
            text={'data' in value ? value.data.Text : undefined}
            pendingFix={'pendingFix' in value ? value.pendingFix : undefined}
          />
        );
      }
      if (isOrderRow(value.data) && isOfEnum(value.status, OrdStatusEnum)) {
        return <OrderStatus ordStatus={value.status} isCustomerOrder={false} />;
      }
      if (isQuoteRow(value.data) && isOfEnum(value.status, QuoteStatusEnum)) {
        return <QuoteStatus quoteStatus={value.status} />;
      }
      if (isLegRow(value.data) && isOfEnum(value.status, QuoteStatusEnum)) {
        return <QuoteStatus quoteStatus={value.status} />;
      }
      return null;
    },
    valueFormatter: ({ value }: ValueFormatterParams<unknown, StatusColumnValue>) => {
      if (!value) {
        return '';
      }

      // The statusText is the status "label" displayed to the user in the cell renderer. We display the same status in exports here,
      // and also use it for sorting comparisons below in the comparator
      return value.statusText;
    },
    comparator: (valueA: StatusColumnValue, valueB: StatusColumnValue) =>
      stringColumnComparator(valueA?.statusText, valueB?.statusText),
    cellClassRules: legRowFadedCellClassRules,
  },
});
